import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  RiFireLine, RiStarLine, RiBookMarkedLine, RiMedalLine,
  RiFlashlightLine, RiFileTextLine, RiBookOpenLine,
  RiCheckLine, RiCloseLine, RiTrophyLine, RiErrorWarningLine,
  RiTimeLine, RiArrowRightLine, RiAlertLine,
  RiRoadMapLine, RiFullscreenLine, RiGridLine, RiQuillPenLine,
  RiCalendarLine, RiFireFill, RiCheckboxCircleFill,
} from 'react-icons/ri';
import { C, LEVEL_COLORS } from '../../data/constants';
import { useAppLevel } from '../../components/app/AppTopbar';
import LevelEmpty from '../../components/app/LevelEmpty';
import { Tag, ProgressBar, EmptyState, LoadingScreen, ModalShell } from '../../components/shared/UI';
import { useAuth } from '../../context/AuthContext';
import {
  getVocabulary, getKanji, getVocabularySession, updateVocabularyMastery,
  getKanjiProgress, getKanjiSession, updateKanjiProgress, getKanaProgress,
} from '../../api/content.api';
import { HIRAGANA, KATAKANA } from '../../data/kanaData';
import { buildWeekStreak, isStudiedToday } from '../../utils/streak';
import { ALL_NODES, SECTIONS, nodeHref } from '../../data/curriculum';
import { getGrammarChapters } from '../../api/grammar.api';
import StreakCalendarModal from '../../components/app/StreakCalendarModal';
import StreakCelebration from '../../components/app/StreakCelebration';
import SessionComplete from '../../components/app/SessionComplete';
import useStreakCelebration from '../../components/app/useStreakCelebration';
import useStudyGuard from '../../components/app/useStudyGuard';
import { getQuizQuestions, submitQuizAttempt, getUserStats, getRoadmap } from '../../api/quiz.api';
import { playMark } from '../../components/brand/celebrate';

/* Semicolons count as separators too. They were missing, and the effect was
   invisible until you hit a kanji that used one: 電's names field reads
   "Electricity; electric powered", which collapsed into a single accepted
   answer of that whole string — so typing "electricity" was marked wrong. */
const csv = value => String(value || '').split(/[、,;；]/).map(v => v.trim()).filter(Boolean);

/* Levenshtein, normalised to 0..1 against the longer string. Used to tell a
   typo apart from a wrong answer: "electricty" should not cost a point the
   way "mountain" should. */
const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) {
      row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    }
    prev = row;
  }
  return prev[b.length];
};

const similarity = (a, b) => {
  const longest = Math.max(a.length, b.length);
  return longest ? 1 - levenshtein(a, b) / longest : 1;
};

/* Below this a spelling is not a near miss, it is a different word. */
const NEAR_MISS = 0.66;
const capFirst = value => {
  const text = String(value || '').trim();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
};
const capCsv = value => csv(value).map(capFirst).join(', ');
const splitLegacyReadings = value => csv(value).reduce((acc, reading) => {
  if (/[\u30a0-\u30ff]/.test(reading)) acc.onyomi.push(reading);
  else acc.kunyomi.push(reading);
  return acc;
}, { onyomi: [], kunyomi: [] });
const onyomiFor = item => item.onyomi || splitLegacyReadings(item.readings).onyomi.join(', ');
const kunyomiFor = item => item.kunyomi || splitLegacyReadings(item.readings).kunyomi.join(', ');
function ReadingChips({ values, color = C.accent, jp = false, capitalize = false }) {
  return (
    <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
      {csv(values).map(v => (
        <span key={v} style={{ background: `${color}14`, border: `1px solid ${color}32`, color, fontFamily: jp ? "'Noto Sans JP',sans-serif" : undefined, padding: '4px 10px', borderRadius: 7, fontSize: 13, fontWeight: 800 }}>
          {capitalize ? capFirst(v) : v}
        </span>
      ))}
    </div>
  );
}

/* ── Shared helpers ──────────────────────────────────────────── */
function Spinner({ label }) {
  return <LoadingScreen label={label} />;
}

function ErrorMsg({ message, onRetry }) {
  return (
    <div style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: C.red, marginBottom: 16 }}>{message}</div>
      {onRetry && <button className="btn-ghost" onClick={onRetry}>Try Again</button>}
    </div>
  );
}

/* ── Profile-dashboard helpers ───────────────────────────────── */
function formatJoined(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function timeAgo(date) {
  if (!date) return '';
  const diffMs = Date.now() - new Date(date).getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  const diffWeek = Math.floor(diffDay / 7);
  if (diffWeek < 5) return `${diffWeek}w ago`;
  return `${Math.floor(diffDay / 30)}mo ago`;
}

/* Turns a { key: masteryPercent } progress map into headline numbers.
   `pct` is the average mastery across every character in the set, so a set
   nobody has touched reads 0% rather than being silently excluded. */
/* Grammar is measured in parts rather than characters: a chapter is a
   handful of parts, and "12 of 60 parts" is a truer picture of how far
   through the course you are than counting chapters you have opened. */
function summariseGrammar(payload) {
  /* The endpoint answers { passPct, chapters }, not a bare array. Reading
     it as an array silently produced a total of 0, which the card renders
     as "Nothing to study yet" — wrong, and quiet about being wrong. */
  const rows = Array.isArray(payload) ? payload
    : Array.isArray(payload?.chapters) ? payload.chapters
      : [];
  const done = rows.reduce((n, c) => n + (Number(c.partsDone) || 0), 0);
  const total = rows.reduce((n, c) => n + (Number(c.partsTotal) || 0), 0);
  return { started: done, total, pct: total ? Math.round((done / total) * 100) : 0, unit: 'parts done' };
}

/* JLPT progress is the exam section of the learning path — which the server
   already derives from real quiz attempts (so many quizzes at so high a
   score). Reusing it means this card cannot disagree with the path, and
   costs no extra request: the dashboard fetches the roadmap anyway. */
function summariseExam(lessons) {
  const ids = (SECTIONS.find(sec => sec.id === 'exam')?.nodes || []).map(n => n.id);
  const rows = ids.map(id => lessons?.[id]).filter(Boolean);
  if (!rows.length) return { started: 0, total: ids.length, pct: 0, unit: 'steps done' };
  const done = rows.filter(r => r.done).length;
  const pct = Math.round((rows.reduce((sum, r) => sum + Math.min(1, Number(r.ratio) || 0), 0) / rows.length) * 100);
  return { started: done, total: ids.length, pct, unit: 'steps done' };
}

function summariseArea(map, total) {
  const values = Object.values(map || {}).map(Number).filter(Number.isFinite);
  const started = values.filter(v => v > 0).length;
  const sum = values.reduce((acc, v) => acc + Math.max(0, Math.min(100, v)), 0);
  return { started, total, pct: total ? Math.round(sum / total) : 0 };
}

function AreaCard({ glyph, label, area, color, onClick }) {
  /* Defaulted rather than assumed. Per-area progress is supplementary — it
     is fetched with allSettled precisely so a failure cannot break the page
     — but reading `area.pct` off an undefined threw during render, which
     the error boundary turned into a full-page crash. A missing area should
     cost you one grey card, not the profile. */
  const { pct = 0, started = 0, total = 0, unit } = area || {};

  return (
    <button type="button" className="ks-card area-card" onClick={onClick} style={{ '--area-color': color }}>
      <span className="area-card-glyph">{glyph}</span>
      <span className="area-card-body">
        <span className="area-card-top">
          <strong>{label}</strong>
          <em>{pct}%</em>
        </span>
        <span className="area-card-track">
          <span className="area-card-fill" style={{ width: `${pct}%` }} />
        </span>
        <span className="area-card-note">
          {total ? `${started} of ${total} ${unit || 'started'}` : 'Nothing to study yet'}
        </span>
      </span>
    </button>
  );
}

function ProfileStat({ icon: Icon, value, label, color = C.accent }) {
  return (
    <div className="ks-card profile-stat-card">
      <span className="profile-stat-icon" style={{ color }}><Icon /></span>
      <span className="profile-stat-copy">
        <span className="profile-stat-value">{value}</span>
        <span className="profile-stat-label">{label}</span>
      </span>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   DASHBOARD (Duolingo-profile-inspired)
══════════════════════════════════════════════════════════════ */
export function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Only N5 is selectable today, but the picker is where we widen this when
  // the other levels ship.
  const { level: selectedLevel } = useAppLevel(); // owned by the app topbar
  const [areas, setAreas] = useState(null);
  const [streakCalOpen, setStreakCalOpen] = useState(false);
  const [roadmapLessons, setRoadmapLessons] = useState({});

  useEffect(() => {
    const load = async () => {
      try {
        const statsRes = await getUserStats();
        setStats(statsRes.data);
        /* Deliberately does NOT set the level. This line used to exist from
           when the picker was Profile-local state; now the topbar owns it
           app-wide, so writing the server's stored level back here silently
           threw away whatever the learner had just selected. */
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();

    // Per-area progress is supplementary — never let it block or break the page.
    Promise.allSettled([
      getKanaProgress(), getKanjiProgress(),
      getKanji({ level: selectedLevel, limit: 1 }), getGrammarChapters(selectedLevel),
    ])
      .then(([kana, kanji, kanjiList, grammar]) => {
        const kanaData  = kana.status === 'fulfilled' ? kana.value.data : null;
        const kanjiData = kanji.status === 'fulfilled' ? kanji.value.data : null;
        const kanjiTotal = kanjiList.status === 'fulfilled' ? (kanjiList.value.total || 0) : 0;
        const grammarData = grammar.status === 'fulfilled' ? grammar.value.data : null;
        setAreas({
          hiragana: summariseArea(kanaData?.hiragana, HIRAGANA.length),
          katakana: summariseArea(kanaData?.katakana, KATAKANA.length),
          kanji:    summariseArea(kanjiData?.kanji, kanjiTotal),
          grammar:  summariseGrammar(grammarData),
        });
      });

    // Drives "next lesson" in Today's plan. Supplementary — a failure here
    // just falls back to suggesting the first lesson.
    getRoadmap()
      .then(res => setRoadmapLessons(res.data.lessons || {}))
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Spinner label="Loading your dashboard" />;
  if (error) return <ErrorMsg message={error} onRetry={() => window.location.reload()} />;

  const firstName = user?.name?.split(' ')[0] || 'Learner';
  const initial = (user?.name || 'L').trim().charAt(0).toUpperCase();

  const hour = new Date().getHours();
  const greeting = hour < 5 ? 'Good night' : hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  // Completion lives on the server (derived from real mastery). Walk the same
  // curriculum the learning path renders, so "Today's plan" can't point at a
  // step the path no longer has.
  const nextNode = ALL_NODES.find(n => !roadmapLessons[n.id]?.done) || null;
  const nextLesson = nextNode && {
    ...nextNode,
    chapterTitle: nextNode.sectionTitle,
    // Pass the lesson through so this resumes at the stage the learner is
    // actually on, rather than restarting them on flashcards every time.
    route: nodeHref(
      nextNode,
      SECTIONS.find(sec => sec.id === nextNode.sectionId),
      roadmapLessons[nextNode.id],
    ),
  };

  const streak = stats?.streak ?? 0;
  const xp = stats?.xp ?? 0;
  const levelColor = LEVEL_COLORS[selectedLevel] || C.accent;
  const masteredPct = stats?.totalCards ? Math.min(100, Math.round(((stats?.masteredCards ?? 0) / stats.totalCards) * 100)) : 0;
  const joined = formatJoined(user?.createdAt);
  // Prefer the value this page just fetched itself — `user` comes from
  // AuthContext, which is only refreshed at login/getMe and can still be
  // showing last session's lastStudied for a beat after grading something.
  const lastStudied = stats?.lastStudied ?? user?.lastStudied;
  const weekCells = buildWeekStreak(streak, lastStudied);
  const doneToday = isStudiedToday(lastStudied);
  const recentQuizzes = stats?.recentQuizzes || [];

  return (
    <div className="page-container profile-page anim-fade-up">
      <div className="profile-grid">
        <div className="profile-main">
          <div className="ks-card profile-banner">
            <div className="profile-banner-glyphs" aria-hidden="true">
              <span>語</span><span>学</span><span>火</span><span>日</span><span>道</span>
            </div>

            <div className="profile-banner-badge">
              <RiFireLine size={14} />
              {streak > 0 ? `${streak}-day streak` : 'Start your streak today'}
            </div>

            <div className="profile-identity">
              <div className="profile-avatar">{initial}</div>
              <div>
                <h1>{firstName}</h1>
                <div className="profile-tagline">{greeting} · learning Japanese with KitsuSpeak</div>
                <div className="profile-meta-row">
                  {joined && <span><RiCalendarLine size={13} /> Joined {joined}</span>}
                  <span><RiBookMarkedLine size={13} /> {stats?.masteredCards ?? 0} cards mastered</span>
                </div>
              </div>
            </div>

            <div className="profile-cta-row">
              <button className="btn-primary" onClick={() => navigate('/dashboard')}>
                <RiRoadMapLine /> Continue learning
              </button>
              <button className="btn-ghost" onClick={() => navigate('/flashcards')}>
                <RiFlashlightLine /> Review cards
              </button>
            </div>

            <div className="level-progress" style={{ '--level-color': levelColor }}>
              <div className="level-progress-head">
                <span className="level-progress-title">{selectedLevel} progress</span>
                <span className="level-progress-pct">{masteredPct}%</span>
              </div>
              <div className="level-progress-track">
                <div className="level-progress-fill" style={{ width: `${masteredPct}%` }} />
              </div>
              <div className="level-progress-legend">
                <span><b>{stats?.masteredCards ?? 0}</b> mastered</span>
                <span><b>{stats?.totalCards ?? 0}</b> started</span>
                <span><b>{stats?.dueCount ?? 0}</b> due</span>
              </div>
            </div>
          </div>

          <section className="dashboard-section stats-section">
            <div className="dashboard-section-head">
              <div>
                <h2>Statistics</h2>
                <p>Your learning footprint at a glance.</p>
              </div>
            </div>
            <div className="profile-stat-grid">
              <ProfileStat icon={RiFireLine} value={streak} label="Day streak" color={C.accent} />
              <ProfileStat icon={RiStarLine} value={xp.toLocaleString()} label="Total XP" color={C.yellow} />
              <ProfileStat icon={RiBookMarkedLine} value={stats?.masteredCards ?? 0} label="Mastered" color={C.blue} />
              <ProfileStat icon={RiMedalLine} value={stats?.dueCount ?? 0} label="Due today" color={C.green} />
            </div>
          </section>

          <section className="dashboard-section areas-section">
            <div className="dashboard-section-head">
              <div>
                <h2>Progress by area</h2>
                <p>How far you&apos;ve come in each part of {selectedLevel}.</p>
              </div>
            </div>
            {areas ? (
              <div className="area-grid">
                <AreaCard glyph="あ" label="Hiragana" area={areas.hiragana} color={C.accent} onClick={() => navigate('/kana')} />
                <AreaCard glyph="ア" label="Katakana" area={areas.katakana} color={C.blue} onClick={() => navigate('/kana')} />
                <AreaCard glyph="漢" label="Kanji" area={areas.kanji} color={C.yellow} onClick={() => navigate('/kanji')} />
                <AreaCard glyph="文" label="Grammar" area={areas.grammar} color={C.purple} onClick={() => navigate('/grammar')} />
                <AreaCard glyph="N5" label="JLPT" area={summariseExam(roadmapLessons)} color={C.red} onClick={() => navigate('/quiz')} />
              </div>
            ) : (
              <div className="area-grid">
                {[0, 1, 2, 3, 4].map(i => <div key={i} className="ks-card area-card is-loading" />)}
              </div>
            )}
          </section>
        </div>

        <aside className="profile-aside">
          <div className={`ks-card profile-week-card${doneToday ? ' done-today' : ''}`}>
            <div className="profile-week-head">
              <div>
                <div className="profile-week-title">This week</div>
                <div className="profile-week-sub">{streak > 0 ? `${streak}-day streak` : 'No active streak yet'}</div>
              </div>
              <button
                type="button"
                className="profile-week-flame"
                onClick={() => setStreakCalOpen(true)}
                title="View streak calendar"
              >
                {doneToday ? <RiFireFill size={20} /> : <RiFireLine size={20} />}
              </button>
            </div>

            <div className="profile-week-status">
              {doneToday
                ? <><RiCheckboxCircleFill size={15} /> Today&apos;s streak is done</>
                : <><RiTimeLine size={15} /> Study today to keep your streak</>}
            </div>

            <div className="profile-week-grid">
              {weekCells.map((cell, i) => (
                <div key={i} className={`profile-week-day${cell.active ? ' active' : ''}${cell.isToday ? ' today' : ''}`}>
                  <span className="profile-week-day-label">{cell.label}</span>
                  <span className="profile-week-day-dot">
                    {cell.active ? <RiFireFill size={15} /> : <RiFireLine size={14} />}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="dashboard-plan-card ks-card">
            <div className="dashboard-plan-head">
              <div>
                <div className="dashboard-plan-title">Today&apos;s plan</div>
                <div className="dashboard-plan-sub">Three quick steps to keep your momentum</div>
              </div>
              <div className={`dashboard-plan-pill${(stats?.dueCount ?? 0) > 0 ? ' due' : ' clear'}`}>
                {(stats?.dueCount ?? 0) > 0 ? <><RiAlertLine size={13} /> {stats.dueCount} due</> : <>All clear</>}
              </div>
            </div>

            <div className="dashboard-plan-list">
              <button type="button" className="dashboard-plan-item" onClick={() => navigate('/flashcards')} style={{ '--plan-color': C.accent }}>
                <span className="dashboard-plan-icon"><RiFlashlightLine /></span>
                <span className="dashboard-plan-copy">
                  <strong>Review flashcards</strong>
                  <span>{(stats?.dueCount ?? 0) > 0 ? `${stats.dueCount} cards due today` : "No cards due — you're ahead"}</span>
                </span>
                <span className="dashboard-plan-cta">Review <RiArrowRightLine size={14} /></span>
              </button>

              <button type="button" className="dashboard-plan-item" onClick={() => navigate(nextLesson ? nextLesson.route : '/dashboard')} style={{ '--plan-color': C.blue }}>
                <span className="dashboard-plan-icon"><RiBookOpenLine /></span>
                <span className="dashboard-plan-copy">
                  <strong>{nextLesson ? nextLesson.title : 'Follow the path'}</strong>
                  <span>{nextLesson ? `${nextLesson.chapterTitle} · ${nextLesson.sub}` : 'Start your guided N5 path'}</span>
                </span>
                <span className="dashboard-plan-cta">Start <RiArrowRightLine size={14} /></span>
              </button>

              <button type="button" className="dashboard-plan-item" onClick={() => navigate('/quiz')} style={{ '--plan-color': C.green }}>
                <span className="dashboard-plan-icon"><RiFileTextLine /></span>
                <span className="dashboard-plan-copy">
                  <strong>Take a JLPT quiz</strong>
                  <span>Test what you learned today</span>
                </span>
                <span className="dashboard-plan-cta">Quiz <RiArrowRightLine size={14} /></span>
              </button>
            </div>
          </div>

          <div className="ks-card profile-activity-card">
            <div className="profile-activity-head">Recent activity</div>
            {recentQuizzes.length === 0 ? (
              <EmptyState icon={RiTrophyLine} title="No quizzes yet" sub="Take a quiz to start building your history." />
            ) : (
              <div className="profile-activity-list">
                {recentQuizzes.slice(0, 4).map((q, i) => {
                  const pct = q.total ? Math.round((q.score / q.total) * 100) : 0;
                  return (
                    <div key={q._id || i} className="profile-activity-item">
                      <span className="profile-activity-icon"><RiTrophyLine /></span>
                      <span className="profile-activity-copy">
                        <strong>{q.score}/{q.total} on {q.level} Quiz</strong>
                        <span>{pct}% score · {timeAgo(q.createdAt)}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </aside>
      </div>

      {streakCalOpen && (
        <StreakCalendarModal lastStudied={lastStudied} onClose={() => setStreakCalOpen(false)} />
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════
   FLASHCARDS
══════════════════════════════════════════════════════════════ */
export function Flashcards() {
  const { level } = useAppLevel();
  const [cards, setCards] = useState([]);
  const [index, setIndex] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showEx, setShowEx] = useState(false);
  const [known, setKnown] = useState([]);
  const [unknown, setUnknown] = useState([]);
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [fullscreen, setFullscreen] = useState(false);
  const [viewAll, setViewAll] = useState(false);
  const [catalogPage, setCatalogPage] = useState(1);
  const [catalog, setCatalog] = useState({ data: [], total: 0, pages: 1 });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [selectedCardFlipped, setSelectedCardFlipped] = useState(false);
  const [selectedCardShowEx, setSelectedCardShowEx] = useState(false);

  const loadCards = () => {
    setLoading(true); setError('');
    getVocabularySession({ level })
      .then(res => { setCards(res.data); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadCards(); }, [level]);

  // Lock background scroll while the fullscreen study overlay is open.
  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [fullscreen]);

  const card = cards[index];
  const total = cards.length;
  const reviewed = known.length + unknown.length;
  const pct = total ? (reviewed / total) * 100 : 0;

  /* The streak screen used to fire only on the kana page, so finishing a
     flashcard run earned the day in silence. */
  const { celebration, dismiss: dismissCelebration } = useStreakCelebration(done);

  /* Dirty from the first answer until the session ends. Before the first
     answer there is nothing to lose, and once `done` is set the work is
     already banked — prompting in either case is just noise. */
  useStudyGuard(reviewed > 0 && !done);

  const next = async (result) => {
    try { await updateVocabularyMastery({ vocabularyId: card._id, correct: result === 'know' }); } catch (_) { }
    if (result === 'know') setKnown(k => [...k, index]); else setUnknown(u => [...u, index]);
    setFlipped(false); setShowEx(false);
    if (index + 1 >= total) { setDone(true); return; }
    setTimeout(() => setIndex(i => i + 1), 180);
  };

  const restart = () => {
    setIndex(0); setFlipped(false); setShowEx(false);
    setKnown([]); setUnknown([]); setDone(false);
    loadCards();
  };

  useEffect(() => {
    if (!viewAll) return;
    setCatalogLoading(true);
    getVocabulary({ level, page: catalogPage, limit: 100 })
      .then(res => setCatalog({ data: res.data || [], total: res.total || 0, pages: res.pages || 1 }))
      .finally(() => setCatalogLoading(false));
  }, [viewAll, catalogPage, level]);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg message={error} onRetry={loadCards} />;
  // An empty level is an expected state, not a failure — this used to
  // render as a red ErrorMsg with a Retry that could never help.
  if (!cards.length) {
    return (
      <div style={{ padding: 32 }} className="anim-fade-up">
        <LevelEmpty kind="vocabulary" />
      </div>
    );
  }

  if (viewAll) return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 22 }}>
        <div><h1 style={{ fontSize: 22, fontWeight: 900, marginBottom: 4 }}>All Flashcards</h1><span style={{ color: C.muted, fontSize: 13 }}>{catalog.total} vocabulary items</span></div>
        <button className="btn-ghost" onClick={() => { setViewAll(false); setSelectedCard(null); }}>Back to study</button>
      </div>
      {catalogLoading ? <Spinner /> : <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(210px,1fr))', gap: 10 }}>
          {catalog.data.map(word => <div key={word._id} className="ks-card" onClick={() => setSelectedCard(word)} style={{ padding: 14, cursor: 'pointer', borderColor: selectedCard?._id === word._id ? C.accentBorder : undefined, background: selectedCard?._id === word._id ? C.accentDim : undefined }}>
            <div style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 24, fontWeight: 900 }}>{word.kanji}</div>
            <div style={{ color: C.muted, fontSize: 13, marginTop: 3 }}>{word.kana}</div>
            <div style={{ color: C.accent, fontSize: 14, fontWeight: 800, marginTop: 8 }}>{word.meaning}</div>
          </div>)}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 24 }}>
          <button className="btn-ghost" disabled={catalogPage <= 1} onClick={() => setCatalogPage(page => page - 1)}>Previous</button>
          <span style={{ color: C.muted, fontSize: 13, fontWeight: 800 }}>Page {catalogPage} of {catalog.pages}</span>
          <button className="btn-ghost" disabled={catalogPage >= catalog.pages} onClick={() => setCatalogPage(page => page + 1)}>Next</button>
        </div>
      </>}

      {selectedCard && createPortal(
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, background: C.bg, display: 'flex', flexDirection: 'column' }}>
          {/* Header */}
          <div style={{ height: 64, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 26px', borderBottom: `1px solid ${C.border}`, background: 'rgba(8,8,14,0.94)', backdropFilter: 'blur(18px)' }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900 }}>Vocabulary Card</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                <Tag color={C.accent}>N5</Tag>
                <span style={{ color: C.muted, fontSize: 12 }}>{selectedCard.type}</span>
              </div>
            </div>
            <button className="btn-icon" onClick={() => { setSelectedCard(null); setSelectedCardFlipped(false); setSelectedCardShowEx(false); }} title="Close">
              <RiCloseLine size={18} />
            </button>
          </div>

          {/* Content */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
            <div style={{ width: '100%', maxWidth: 860 }}>
              {/* Flashcard */}
              <div
                onClick={() => setSelectedCardFlipped(f => !f)}
                style={{
                  background: C.card,
                  border: `1px solid ${selectedCardFlipped ? C.accentBorder : C.border}`,
                  borderRadius: 18,
                  padding: '72px 56px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  marginBottom: 16,
                  transition: 'border-color 0.3s',
                  minHeight: 460,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 10,
                  position: 'relative'
                }}
              >
                {/* Type tag */}
                <div style={{ position: 'absolute', top: 14, right: 14 }}>
                  <Tag color={selectedCard.type === 'verb' ? C.blue : selectedCard.type === 'adj' ? C.green : C.muted}>
                    {selectedCard.type}
                  </Tag>
                </div>

                {/* Level tag */}
                <div style={{ position: 'absolute', top: 14, left: 14 }}>
                  <Tag color={C.accent}>N5</Tag>
                </div>

                {/* Kanji */}
                <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 110, fontWeight: 900, lineHeight: 1 }}>
                  {selectedCard.kanji}
                </div>

                {/* Kana */}
                <div style={{ fontSize: 26, color: C.muted }}>
                  {selectedCard.kana}
                </div>
                {selectedCard.romaji && <div style={{ fontSize: 17, color: C.faint, fontWeight: 800 }}>{selectedCard.romaji}</div>}

                {/* Flipped content */}
                {selectedCardFlipped ? (
                  <div className="anim-pop" style={{ width: '100%', textAlign: 'center' }}>
                    <div style={{ height: 1, background: C.border, margin: '14px 0' }} />
                    <div style={{ fontSize: 24, fontWeight: 800, color: C.accent }}>
                      {selectedCard.meaning}
                    </div>
                    <div
                      style={{ fontSize: 13, color: C.muted, marginTop: 8, cursor: 'pointer' }}
                      onClick={e => {
                        e.stopPropagation();
                        setSelectedCardShowEx(s => !s);
                      }}
                    >
                      {selectedCardShowEx ? 'Hide' : 'Show'} example
                    </div>
                    {selectedCardShowEx && (
                      <div className="anim-fade-in" style={{ marginTop: 14, padding: '14px 18px', background: C.surface, borderRadius: 12 }}>
                        <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: 18, marginBottom: 6 }}>
                          {selectedCard.example}
                        </div>
                        <div style={{ fontSize: 14, color: C.muted }}>
                          {selectedCard.exampleEn}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div style={{ fontSize: 13, color: C.faint, marginTop: 8 }}>
                    Tap to reveal meaning
                  </div>
                )}
              </div>

              {/* Bottom buttons */}
              {selectedCardFlipped ? (
                <div className="anim-fade-in" style={{ display: 'flex', gap: 10 }}>
                  <button
                    className="btn-ghost"
                    style={{ flex: 1, justifyContent: 'center', borderColor: `${C.red}35`, color: C.red }}
                    onClick={() => {
                      setSelectedCard(null);
                      setSelectedCardFlipped(false);
                      setSelectedCardShowEx(false);
                    }}
                  >
                    <RiCloseLine size={17} />
                    Close
                  </button>
                  <button
                    className="btn-primary"
                    style={{ flex: 1, justifyContent: 'center', background: C.green }}
                    onClick={() => {
                      setSelectedCard(null);
                      setSelectedCardFlipped(false);
                      setSelectedCardShowEx(false);
                    }}
                  >
                    <RiCheckLine size={17} />
                    Done
                  </button>
                </div>
              ) : (
                <div style={{ textAlign: 'center', fontSize: 12, color: C.faint }}>
                  Click card to flip · Close when done
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );

  if (done) return (
    <div style={{ padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
      <SessionComplete
        scorePct={reviewed ? Math.round((known.length / reviewed) * 100) : 0}
        correct={known.length}
        total={reviewed}
        label="remembered"
        title="Session complete!"
        detail={unknown.length
          ? `${unknown.length} to review — they will come round again sooner.`
          : 'Nothing left to review. Everything stuck.'}
        onRetry={restart}
        retryLabel="Study again"
      />
      {celebration && (
        <StreakCelebration
          streak={celebration.streak}
          lastStudied={celebration.lastStudied}
          onContinue={dismissCelebration}
        />
      )}
    </div>
  );
  return (
    <>
      <div
        style={{
          padding: 32,
          width: '100%',
          '--stage-w': '536px',
        }}
        className="anim-fade-up study-stage"
      >
        <div className="study-stage-body" style={{ flex: 1 }}>
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: 20,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: 21,
                fontWeight: 900,
                letterSpacing: -0.4,
                marginBottom: 4
              }}
            >
              Flashcards
            </h1>

            <div
              style={{
                display: 'flex',
                gap: 7,
                alignItems: 'center'
              }}
            >
              <Tag color={C.accent}>N5</Tag>

              <span
                style={{
                  fontSize: 12,
                  color: C.muted
                }}
              >
                {index + 1} / {total}
              </span>
              <button className="btn-ghost" style={{ padding: '5px 10px', fontSize: 12 }} onClick={() => { setCatalogPage(1); setViewAll(true); }}>View all</button>
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14
            }}
          >
            <div
              style={{
                display: 'flex',
                gap: 14,
                fontSize: 13
              }}
            >
              <span style={{ color: C.green, fontWeight: 700 }}>
                ✓ {known.length}
              </span>

              <span style={{ color: C.red, fontWeight: 700 }}>
                ✗ {unknown.length}
              </span>
            </div>

            <button
              className="btn-ghost"
              onClick={() => setFullscreen(true)}
              title="Open full screen"
              style={{
                width: 42,
                height: 42,
                padding: 0,
                justifyContent: 'center'
              }}
            >
              <RiFullscreenLine size={18} />
            </button>
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <ProgressBar value={pct} />
        </div>

        {/* Flashcard */}
        <div
          onClick={() => setFlipped(f => !f)}
          style={{
            background: C.card,
            border: `1px solid ${flipped ? C.accentBorder : C.border
              }`,
            borderRadius: 18,
            padding: '48px 36px',
            textAlign: 'center',
            cursor: 'pointer',
            marginBottom: 16,
            transition: 'border-color 0.3s',
            minHeight: 280,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            position: 'relative'
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: 14,
              right: 14
            }}
          >
            <Tag
              color={
                card.type === 'verb'
                  ? C.blue
                  : card.type === 'adj'
                    ? C.green
                    : C.muted
              }
            >
              {card.type}
            </Tag>
          </div>

          <div
            style={{
              position: 'absolute',
              top: 14,
              left: 14
            }}
          >
            <Tag color={C.accent}>N5</Tag>
          </div>

          <div
            style={{
              fontFamily:
                "'Noto Sans JP',sans-serif",
              fontSize: 68,
              fontWeight: 900,
              lineHeight: 1
            }}
          >
            {card.kanji}
          </div>

          <div
            style={{
              fontSize: 18,
              color: C.muted
            }}
          >
            {card.kana}
          </div>
          {card.romaji && <div style={{ fontSize: 14, color: C.faint, fontWeight: 800 }}>{card.romaji}</div>}

          {flipped ? (
            <div
              className="anim-pop"
              style={{
                width: '100%',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  height: 1,
                  background: C.border,
                  margin: '10px 0'
                }}
              />

              <div
                style={{
                  fontSize: 20,
                  fontWeight: 800,
                  color: C.accent
                }}
              >
                {card.meaning}
              </div>

              <div
                style={{
                  fontSize: 12,
                  color: C.muted,
                  marginTop: 6,
                  cursor: 'pointer'
                }}
                onClick={e => {
                  e.stopPropagation();
                  setShowEx(s => !s);
                }}
              >
                {showEx ? 'Hide' : 'Show'} example
              </div>

              {showEx && (
                <div
                  className="anim-fade-in"
                  style={{
                    marginTop: 10,
                    padding: '10px 14px',
                    background: C.surface,
                    borderRadius: 9
                  }}
                >
                  <div
                    style={{
                      fontFamily:
                        "'Noto Sans JP',sans-serif",
                      fontSize: 14,
                      marginBottom: 3
                    }}
                  >
                    {card.example}
                  </div>

                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted
                    }}
                  >
                    {card.exampleEn}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              style={{
                fontSize: 12,
                color: C.faint,
                marginTop: 6
              }}
            >
              Tap to reveal meaning
            </div>
          )}
        </div>

        </div>

        <div className="study-footer">
          <div className="study-footer-inner">
            <button className="btn-ghost" disabled={!flipped} onClick={() => next('unknown')}
                    style={{ flex: 1, justifyContent: 'center', borderColor: `${C.red}35`, color: C.red, opacity: flipped ? 1 : 0.34 }}>
              <RiCloseLine size={17} /> Still learning
            </button>
            <button className="btn-primary" disabled={!flipped} onClick={() => next('know')}
                    style={{ flex: 1, justifyContent: 'center', background: C.green, marginLeft: 0 }}>
              <RiCheckLine size={17} /> Got it
            </button>
          </div>
        </div>
      </div >

      {/* FULLSCREEN OVERLAY
          Portaled to <body>: .app-main sets `isolation: isolate` and gives its
          children `z-index: 1`, so rendering in place would trap this overlay
          in that stacking context — the fixed mobile header (z-index 1000)
          would then paint over the close button. */}
      {
        fullscreen && createPortal(
          <div
            className="study-fullscreen-shell"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1200,
              background: C.bg,
              display: 'flex',
              flexDirection: 'column',
              padding: 0,
              overflowY: 'auto',
            }}
          >
            {/* Header */}
            <div
              className="study-fullscreen-header"
              style={{
                flexShrink: 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '14px 32px',
                background: 'rgba(8,8,14,0.94)',
                backdropFilter: 'blur(18px)',
                borderBottom: `1px solid ${C.border}`,
                position: 'sticky',
                top: 0,
                zIndex: 1,
              }}
            >
              <h2
                style={{
                  fontSize: 22,
                  fontWeight: 900,
                }}
              >
                Flashcards
              </h2>

              <button
                className="btn-icon study-fullscreen-close"
                onClick={() => setFullscreen(false)}
                aria-label="Exit full screen"
                title="Exit full screen"
              >
                <RiCloseLine size={18} />
              </button>
            </div>

            {/* Main content */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 32 }}>
              <div
                className="study-stage fullscreen"
                style={{ width: '100%', '--stage-w': '860px' }}
              >
                <div className="study-stage-body">
                {/* Count row */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 14,
                    fontSize: 13,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <Tag color={C.accent}>N5</Tag>

                    <span
                      style={{
                        color: C.muted,
                      }}
                    >
                      {index + 1} / {total}
                    </span>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      gap: 14,
                    }}
                  >
                    <span
                      style={{
                        color: C.green,
                        fontWeight: 700,
                      }}
                    >
                      ✓ {known.length}
                    </span>

                    <span
                      style={{
                        color: C.red,
                        fontWeight: 700,
                      }}
                    >
                      ✗ {unknown.length}
                    </span>
                  </div>
                </div>

                {/* Progress */}
                <div style={{ marginBottom: 24 }}>
                  <ProgressBar value={pct} />
                </div>

                {/* Flashcard */}
                <div
                  onClick={() => setFlipped(f => !f)}
                  style={{
                    background: C.card,
                    border: `1px solid ${flipped
                      ? C.accentBorder
                      : C.border
                      }`,
                    borderRadius: 18,
                    padding: '72px 56px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    marginBottom: 16,
                    transition: 'border-color 0.3s',
                    minHeight: 460,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 10,
                    position: 'relative',
                  }}
                >
                  {/* Type tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                    }}
                  >
                    <Tag
                      color={
                        card.type === 'verb'
                          ? C.blue
                          : card.type === 'adj'
                            ? C.green
                            : C.muted
                      }
                    >
                      {card.type}
                    </Tag>
                  </div>

                  {/* Level tag */}
                  <div
                    style={{
                      position: 'absolute',
                      top: 14,
                      left: 14,
                    }}
                  >
                    <Tag color={C.accent}>N5</Tag>
                  </div>

                  {/* Kanji */}
                  <div
                    style={{
                      fontFamily:
                        "'Noto Sans JP', sans-serif",
                      fontSize: 110,
                      fontWeight: 900,
                      lineHeight: 1,
                    }}
                  >
                    {card.kanji}
                  </div>

                  {/* Kana */}
                  <div
                    style={{
                      fontSize: 26,
                      color: C.muted,
                    }}
                  >
                    {card.kana}
                  </div>
                  {card.romaji && <div style={{ fontSize: 17, color: C.faint, fontWeight: 800 }}>{card.romaji}</div>}

                  {/* Flipped content */}
                  {flipped ? (
                    <div
                      className="anim-pop"
                      style={{
                        width: '100%',
                        textAlign: 'center',
                      }}
                    >
                      <div
                        style={{
                          height: 1,
                          background: C.border,
                          margin: '14px 0',
                        }}
                      />

                      <div
                        style={{
                          fontSize: 24,
                          fontWeight: 800,
                          color: C.accent,
                        }}
                      >
                        {card.meaning}
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          color: C.muted,
                          marginTop: 8,
                          cursor: 'pointer',
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          setShowEx(s => !s);
                        }}
                      >
                        {showEx ? 'Hide' : 'Show'} example
                      </div>

                      {showEx && (
                        <div
                          className="anim-fade-in"
                          style={{
                            marginTop: 14,
                            padding: '14px 18px',
                            background: C.surface,
                            borderRadius: 12,
                          }}
                        >
                          <div
                            style={{
                              fontFamily:
                                "'Noto Sans JP', sans-serif",
                              fontSize: 18,
                              marginBottom: 6,
                            }}
                          >
                            {card.example}
                          </div>

                          <div
                            style={{
                              fontSize: 14,
                              color: C.muted,
                            }}
                          >
                            {card.exampleEn}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div
                      style={{
                        fontSize: 13,
                        color: C.faint,
                        marginTop: 8,
                      }}
                    >
                      Tap to reveal meaning
                    </div>
                  )}
                </div>

                </div>

                <div className="study-footer">
                  <div className="study-footer-inner">
                    <button className="btn-ghost" disabled={!flipped} onClick={() => next('unknown')}
                            style={{ flex: 1, justifyContent: 'center', borderColor: `${C.red}35`, color: C.red, opacity: flipped ? 1 : 0.34 }}>
                      <RiCloseLine size={17} /> Still learning
                    </button>
                    <button className="btn-primary" disabled={!flipped} onClick={() => next('know')}
                            style={{ flex: 1, justifyContent: 'center', background: C.green, marginLeft: 0 }}>
                      <RiCheckLine size={17} /> Got it
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )
      }
    </>
  );

}

/* ══════════════════════════════════════════════════════════════
   QUIZ
══════════════════════════════════════════════════════════════ */
export function Quiz() {
  const { level } = useAppLevel();
  const [questions, setQuestions] = useState([]);
  const [qIndex, setQIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [done, setDone] = useState(false);
  const [timer, setTimer] = useState(600);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [startTime, setStartTime] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const ref = useRef(null);

  const { celebration, dismiss: dismissCelebration } = useStreakCelebration(done);
  useStudyGuard(answers.length > 0 && !done);

  const loadQuestions = () => {
    setLoading(true); setError('');
    getQuizQuestions({ level, limit: 10 })
      .then(res => {
        setQuestions(Array.isArray(res.data) ? res.data : []);
        setStartTime(Date.now());
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadQuestions(); }, [level]);

  useEffect(() => {
    if (loading || done || !questions.length) return;
    ref.current = setInterval(() => setTimer(t => {
      if (t <= 1) { clearInterval(ref.current); setFullscreen(false); setDone(true); return 0; }
      return t - 1;
    }), 1000);
    return () => clearInterval(ref.current);
  }, [loading, done, questions.length]);

  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [fullscreen]);

  const q = questions[qIndex];
  const mm = String(Math.floor(timer / 60)).padStart(2, '0');
  const ss = String(timer % 60).padStart(2, '0');
  const pct = questions.length ? (qIndex / questions.length) * 100 : 0;

  const choose = i => {
    if (selected !== null) return;
    setSelected(i);
    playMark(i === q.answer ? 'bounce' : 'shake');
  };

  const next = async () => {
    const isCorrect = selected === q.answer;
    const newAnswers = [...answers, { questionId: q._id, correct: isCorrect }];
    setAnswers(newAnswers); setSelected(null);
    if (qIndex + 1 >= questions.length) {
      clearInterval(ref.current);
      const score = newAnswers.filter(a => a.correct).length;
      const duration = Math.round((Date.now() - startTime) / 1000);
      try { await submitQuizAttempt({ level, score, total: questions.length, duration, answers: newAnswers }); } catch (_) { }
      setFullscreen(false);
      setDone(true); return;
    }
    setQIndex(i => i + 1);
  };

  /* Enter advances, once an answer is locked in. Answering with the mouse
     and then reaching for the mouse again to press Next is the kind of
     small friction that makes a ten-question quiz feel long.

     Held in a ref rather than listed as a dependency: `next` is rebuilt on
     every render (it closes over answers, qIndex, selected), so depending
     on it would tear down and re-add the listener constantly. The ref keeps
     the handler pointed at the current one without that churn.

     The activeElement guard is the important part. When the Next button has
     focus, the browser already fires its click on Enter — without this the
     window listener fired too and the quiz jumped two questions. */
  const advanceRef = useRef(next);
  advanceRef.current = next;

  useEffect(() => {
    if (done || loading) return undefined;
    const onKeyDown = event => {
      if (event.key !== 'Enter' || event.repeat) return;
      if (selected === null) return;                         // nothing chosen yet
      if (document.activeElement?.tagName === 'BUTTON') return;  // its own click will fire
      event.preventDefault();
      advanceRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [done, loading, selected]);

  const restart = () => {
    clearInterval(ref.current);
    setQIndex(0); setSelected(null); setAnswers([]);
    setDone(false); setTimer(600);
    loadQuestions();
  };

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg message={error} onRetry={loadQuestions} />;
  if (!questions.length) {
    return (
      <div style={{ padding: 32 }} className="anim-fade-up">
        <div style={{ marginBottom: 22 }}>
          <h1 style={{ fontSize: 21, fontWeight: 900, letterSpacing: -0.4, marginBottom: 4 }}>JLPT Quiz</h1>
          <Tag color={C.accent}>{level} Practice</Tag>
        </div>
        <div className="ks-card">
          <EmptyState
            icon={RiFileTextLine}
            title={`No ${level} quiz questions yet`}
            sub={`Add ${level} questions from the admin quiz manager, then come back to practice.`}
          />
        </div>
      </div>
    );
  }

  if (done) {
    const score = answers.filter(a => a.correct).length;
    const total = questions.length || 1;
    const scorePct = Math.round((score / total) * 100);
    return (
      <div className="study-stage" style={{ padding: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '80vh' }}>
        <SessionComplete
          scorePct={scorePct}
          passed={scorePct >= 60}
          correct={score}
          total={total}
          label="questions"
          title={scorePct >= 80 ? 'Excellent!' : scorePct >= 60 ? 'Good effort!' : 'Keep studying!'}
          detail={scorePct >= 60 ? null : 'Score 60% or more to count this as a pass.'}
          onRetry={restart}
          retryLabel="Try again"
        />
        {celebration && (
          <StreakCelebration
            streak={celebration.streak}
            lastStudied={celebration.lastStudied}
            onContinue={dismissCelebration}
          />
        )}
      </div>
    );
  }

  const renderQuiz = (isFullscreen = false) => (
    <div
      className={`anim-fade-up study-stage quiz-stage${isFullscreen ? ' fullscreen' : ''}`}
      style={{
        padding: isFullscreen ? '22px 24px' : 32,
        width: '100%',
        '--stage-w': isFullscreen ? '872px' : '656px',
      }}
    >
      <div className="study-stage-body">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginBottom: isFullscreen ? 20 : 22 }}>
        <div>
          <h1 style={{ fontSize: isFullscreen ? 23 : 21, fontWeight: 900, letterSpacing: 0, marginBottom: 6 }}>JLPT Quiz</h1>
          <Tag color={C.accent}>N5 Practice</Tag>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: timer < 60 ? `${C.red}16` : C.accentDim, border: `1px solid ${timer < 60 ? `${C.red}35` : C.accentBorder}`, borderRadius: 10, padding: isFullscreen ? '9px 14px' : '7px 13px', color: timer < 60 ? C.red : C.accent, fontWeight: 900, fontSize: isFullscreen ? 16 : 15 }}>
            <RiTimeLine size={isFullscreen ? 16 : 15} />{mm}:{ss}
          </div>
          {!isFullscreen && (
            <button
              className="btn-ghost"
              onClick={() => setFullscreen(true)}
              title="Open quiz full screen"
              style={{ width: 42, height: 42, padding: 0, justifyContent: 'center' }}
            >
              <RiFullscreenLine size={18} />
            </button>
          )}
        </div>
      </div>

      <div className="study-progress" style={{ marginBottom: isFullscreen ? 18 : 24 }}>
        <ProgressBar value={pct} />
      </div>

      <div className="ks-card quiz-question-card" style={{ padding: isFullscreen ? '22px 28px' : 24, marginBottom: isFullscreen ? 16 : 18 }}>
        <div style={{ fontSize: isFullscreen ? 12 : 11, color: C.muted, fontWeight: 800, marginBottom: 10, letterSpacing: 0.5 }}>QUESTION {qIndex + 1} OF {questions.length}</div>
        <h2 style={{ fontSize: isFullscreen ? 23 : 19, fontWeight: 900, lineHeight: 1.3 }}>{q.question}</h2>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: isFullscreen ? 10 : 9 }}>
        {q.options.map((opt, i) => {
          let bg = C.surface, bc = C.border, col = C.text;
          if (selected !== null) {
            if (i === q.answer) { bg = `${C.green}16`; bc = `${C.green}45`; col = C.green; }
            else if (i === selected) { bg = `${C.red}16`; bc = `${C.red}45`; col = C.red; }
          }
          return (
            <button
              key={i}
              className="choice-btn"
              disabled={selected !== null}
              onClick={() => choose(i)}
              style={{
                background: bg,
                borderColor: bc,
                color: col,
                padding: isFullscreen ? '14px 18px' : undefined,
                minHeight: isFullscreen ? 56 : undefined,
                fontSize: isFullscreen ? 16 : undefined
              }}
            >
              <span style={{ fontWeight: 800, color: C.muted, fontSize: isFullscreen ? 12 : 12, minWidth: 24 }}>{['A', 'B', 'C', 'D'][i]}</span>
              <span style={{ flex: 1 }}>{opt}</span>
              {selected !== null && i === q.answer && <RiCheckLine size={isFullscreen ? 16 : 15} style={{ color: C.green }} />}
              {selected !== null && i === selected && selected !== q.answer && <RiCloseLine size={isFullscreen ? 16 : 15} style={{ color: C.red }} />}
            </button>
          );
        })}
      </div>

      </div>

      <div className={`study-footer${selected !== null ? (selected === q.answer ? ' correct' : ' wrong') : ''}`}>
        <div className="study-footer-inner">
          {selected !== null ? (
            <div className="study-feedback">
              <strong>
                {selected === q.answer ? <><RiCheckLine size={15} /> Correct</> : <><RiCloseLine size={15} /> Not quite</>}
              </strong>
              <span>{q.options[q.answer]}</span>
            </div>
          ) : (
            <span className="study-footer-hint">Pick an answer</span>
          )}

          <button className="btn-primary" disabled={selected === null} onClick={next}>
            {qIndex + 1 >= questions.length ? 'See results' : 'Next'} <RiArrowRightLine />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {renderQuiz()}

      {fullscreen && createPortal(
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            background: C.bg,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <div style={{
            height: 64,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 26px',
            borderBottom: `1px solid ${C.border}`,
            background: 'rgba(8,8,14,0.94)',
            backdropFilter: 'blur(18px)',
          }}>
            <div>
              <div style={{ fontSize: 15, fontWeight: 900 }}>JLPT Quiz</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                <Tag color={C.accent}>N5 Practice</Tag>
                <span style={{ color: C.muted, fontSize: 12 }}>{questions.length} questions</span>
              </div>
            </div>
            <button className="btn-icon" onClick={() => setFullscreen(false)} title="Close full screen">
              <RiCloseLine size={18} />
            </button>
          </div>

          {/* Content — fills remaining height, centers quiz, no scroll */}
          <div
            style={{
              flex: 1,
              minHeight: 0,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px',
            }}
          >
            {renderQuiz(true)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════
   KANJI
══════════════════════════════════════════════════════════════ */
function shuffleItems(items) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function meaningForKanji(item) {
  return capFirst(item?.meaning || csv(item?.names)[0] || '');
}

/* The kana reading plus its romaji.

   The two are stored in parallel lists — kunyomi "なが(い)、 おさ" against
   kunyomiEnglish "Naga(i), Osa" — so the romaji is whichever entry sits at
   the same index as the kana we are showing. Which list to look in is not
   fixed: popularReading is sometimes a kun reading and sometimes an on one
   (日 shows ニチ, which is its onyomi), so both are searched rather than
   assuming. Falls back to an empty string rather than a wrong pairing. */
function readingPairForKanji(item) {
  const kun = csv(kunyomiFor(item));
  const kunEn = csv(item?.kunyomiEnglish);
  const on = csv(onyomiFor(item));
  const onEn = csv(item?.onyomiEnglish);

  const romajiFor = jp => {
    const k = kun.indexOf(jp);
    if (k >= 0 && kunEn[k]) return kunEn[k];
    const o = on.indexOf(jp);
    if (o >= 0 && onEn[o]) return onEn[o];
    return '';
  };

  const popular = csv(item?.popularReading)[0];
  if (popular) return { jp: popular, en: romajiFor(popular) };
  if (kun[0]) return { jp: kun[0], en: kunEn[0] || '' };
  if (on[0]) return { jp: on[0], en: onEn[0] || '' };
  return { jp: 'No reading', en: '' };
}

/* The reading as one line: kana, then romaji in the muted colour. Used
   wherever a card shows a single reading, so the four kanji modes stay
   consistent with each other. */
function KanjiReading({ item, size = 12, color = C.muted }) {
  const { jp, en } = readingPairForKanji(item);
  return (
    <span style={{ fontFamily: "'Noto Sans JP',sans-serif", color, fontSize: size, fontWeight: 800 }}>
      {jp}
      {en && (
        <span style={{ fontFamily: "'Nunito',sans-serif", opacity: 0.72, marginLeft: 7, fontWeight: 700 }}>
          {en}
        </span>
      )}
    </span>
  );
}

function kanjiMasteryFor(progress, item) {
  const value = Number(progress?.kanji?.[item?._id] ?? item?.mastery);
  return Number.isFinite(value) ? Math.max(0, Math.min(100, value)) : 0;
}

function makeKanjiSession(items, seed = 0) {
  const sessionOffset = Number(seed) || 0;
  const count = Math.min(items.length, 14 + ((Math.floor(Math.random() * 12) + sessionOffset) % 12));
  return shuffleItems(items).slice(0, count || items.length);
}

function getKanjiOptions(correct, allItems) {
  const correctMeaning = meaningForKanji(correct).toLowerCase();
  const others = shuffleItems(allItems.filter(item => meaningForKanji(item).toLowerCase() !== correctMeaning)).slice(0, 3);
  return shuffleItems([correct, ...others]);
}

function KanjiModeTab({ icon: Icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className="study-mode-tab"
      style={{
        display: 'flex', alignItems: 'center', gap: 7,
        padding: '9px 18px', borderRadius: 9, border: 'none', cursor: 'pointer',
        fontFamily: "'Nunito', sans-serif", fontWeight: 800, fontSize: 14,
        background: active ? C.accent : 'transparent',
        color: active ? '#fff' : C.muted,
        transition: 'all 0.2s',
      }}
    >
      <Icon size={15} />{label}
    </button>
  );
}

function KanjiChartMode({ kanjiList, selected, setSelected, progress, fullscreen = false }) {
  const sorted = [...kanjiList].sort((a, b) => (a.strokes || 0) - (b.strokes || 0) || String(a.char).localeCompare(String(b.char)));
  const chartGrid = (
    <div className="kanji-chart-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(96px,1fr))', gap: 10 }}>
      {sorted.map(k => {
        const active = selected?._id === k._id;
        return (
          <button
            key={k._id}
            type="button"
            className="ks-card kanji-grid-card"
            onClick={() => setSelected(active ? null : k)}
            style={{
              padding: '17px 10px',
              textAlign: 'center',
              cursor: 'pointer',
              borderColor: active ? C.accentBorder : C.border,
              background: active ? C.accentDim : C.card,
            }}
          >
            <div style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 40, fontWeight: 900, marginBottom: 6, lineHeight: 1, color: "rgb(247, 243, 234)"}}>{k.char}</div>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 800 }}>{meaningForKanji(k)}</div>
            <div style={{ marginTop: 6 }}><KanjiReading item={k} size={12} color={C.green} /></div>
            <div style={{ marginTop: 8, height: 4, borderRadius: 999, background: C.surface, overflow: 'hidden' }}>
              <div style={{ width: `${kanjiMasteryFor(progress, k)}%`, height: '100%', borderRadius: 999, background: C.progress }} />
            </div>
            <div style={{ fontSize: 10, color: C.faint, marginTop: 4, fontWeight: 900 }}>{kanjiMasteryFor(progress, k)}%</div>
          </button>
        );
      })}
    </div>
  );

  // One presentation on every screen size: an overlay. (Previously desktop got
  // an inline side panel and only mobile got the overlay — two copies of the
  // same content, and the mobile copy had already fallen behind, missing the
  // writing-practice note.)
  const detailModal = selected && (
    <ModalShell
      onClose={() => setSelected(null)}
      overlayClassName="kanji-modal-overlay"
      boxClassName="kanji-modal-box"
      label={`${selected.char} details`}
    >
      <div style={{ textAlign: 'center', marginBottom: 22 }}>
        <div style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: 84, fontWeight: 900, lineHeight: 1 }}>
          {selected.char}
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>{selected.strokes} strokes</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1, marginBottom: 7, textTransform: 'uppercase' }}>Display Name</div>
        <div style={{ fontSize: 16, fontWeight: 900 }}>{meaningForKanji(selected)}</div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1, marginBottom: 7, textTransform: 'uppercase' }}>
          <span>Mastery</span>
          <span>{kanjiMasteryFor(progress, selected)}%</span>
        </div>
        <ProgressBar value={kanjiMasteryFor(progress, selected)} />
      </div>
      {csv(selected.names || selected.meaning).length > 1 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1, marginBottom: 7, textTransform: 'uppercase' }}>All Names</div>
          <ReadingChips values={selected.names || selected.meaning} color={C.blue} capitalize />
        </div>
      )}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1, marginBottom: 7, textTransform: 'uppercase' }}>Onyomi</div>
        {onyomiFor(selected) ? <ReadingChips values={onyomiFor(selected)} jp /> : <span style={{ fontSize: 12, color: C.faint }}>No onyomi listed</span>}
        {selected.onyomiEnglish && <div style={{ marginTop: 8 }}><ReadingChips values={capCsv(selected.onyomiEnglish)} color={C.muted} /></div>}
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: C.faint, letterSpacing: 1, marginBottom: 7, textTransform: 'uppercase' }}>Kunyomi</div>
        {kunyomiFor(selected) ? <ReadingChips values={kunyomiFor(selected)} jp /> : <span style={{ fontSize: 12, color: C.faint }}>No kunyomi listed</span>}
        {selected.kunyomiEnglish && <div style={{ marginTop: 8 }}><ReadingChips values={capCsv(selected.kunyomiEnglish)} color={C.muted} /></div>}
      </div>
      <div style={{ padding: '12px 14px', background: `${C.yellow}10`, border: `1px solid ${C.yellow}28`, borderRadius: 9, display: 'flex', gap: 9, alignItems: 'flex-start' }}>
        <RiAlertLine size={14} style={{ color: C.yellow, flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.65 }}>Writing practice is available in the Writing tab.</div>
      </div>
      <button className="btn-ghost" style={{ width: '100%', justifyContent: 'center', marginTop: 14 }} onClick={() => setSelected(null)}>Close</button>
    </ModalShell>
  );

  return (
    <div className="kanji-chart-layout">
      {chartGrid}
      {detailModal}
    </div>
  );
}

function KanjiFlashcardMode({ progress, onGrade, flashcardState, setFlashcardState, fullscreen = false }) {
  const { cards = [], index = 0, flipped = false, known = [], unknown = [], done = false } = flashcardState;

  if (!cards.length) {
    return <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>Preparing your kanji flashcards...</div>;
  }

  const card = cards[index];
  const total = cards.length;
  const pct = ((known.length + unknown.length) / total) * 100;

  const next = result => {
    onGrade(card, result === 'know');
    const isKnown = result === 'know';
    const isLast = index + 1 >= total;

    setFlashcardState(prev => ({
      ...prev,
      flipped: false,
      known: isKnown ? [...prev.known, index] : prev.known,
      unknown: isKnown ? prev.unknown : [...prev.unknown, index],
      done: isLast ? true : prev.done,
    }));

    if (!isLast) {
      setTimeout(() => {
        setFlashcardState(prev => ({ ...prev, index: prev.index + 1 }));
      }, 180);
    }
  };

  if (done) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <SessionComplete
          scorePct={(known.length + unknown.length)
            ? Math.round((known.length / (known.length + unknown.length)) * 100) : 0}
          correct={known.length}
          total={known.length + unknown.length}
          label="known"
          title="Session complete!"
          detail={unknown.length ? `${unknown.length} to review.` : 'Every card stuck.'}
          onRetry={() => window.location.reload()}
          retryLabel="Study again"
        />
      </div>
    );
  }

  return (
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': fullscreen ? '720px' : '520px' }}>
      <div className="study-stage-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 13, color: C.muted }}>{index + 1} / {total}</span>
        <div style={{ display: 'flex', gap: 12, fontSize: 13 }}>
          <span style={{ color: C.muted, fontWeight: 800 }}>Mastery {kanjiMasteryFor(progress, card)}%</span>
          <span style={{ color: C.green, fontWeight: 700 }}>✓ {known.length}</span>
          <span style={{ color: C.red, fontWeight: 700 }}>✗ {unknown.length}</span>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}><ProgressBar value={pct} /></div>

      <div
        onClick={() => setFlashcardState(prev => ({ ...prev, flipped: !prev.flipped }))}
        style={{
          background: C.card,
          border: `1px solid ${flipped ? C.accentBorder : C.border}`,
          borderRadius: 20,
          padding: fullscreen ? '44px 36px' : '40px 32px',
          textAlign: 'center',
          cursor: 'pointer',
          minHeight: fullscreen ? 260 : 230,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          transition: 'border-color 0.3s',
          marginBottom: 16,
        }}
      >
        <div style={{ fontFamily: "'Noto Sans JP', sans-serif", fontSize: fullscreen ? 96 : 78, fontWeight: 900, lineHeight: 1 }}>
          {card.char}
        </div>
        {/* Both faces share one well, sized to the taller of the two, so
            revealing the answer cannot change the card's height and slide
            the glyph above it. */}
        <div style={{ minHeight: fullscreen ? 86 : 76, display: 'flex', flexDirection: 'column', justifyContent: 'center', width: '100%' }}>
          {flipped ? (
            <div className="anim-pop">
              <div style={{ height: 1, background: C.border, margin: '0 0 12px' }} />
              <div style={{ fontSize: fullscreen ? 28 : 24, fontWeight: 900, color: C.accent }}>{meaningForKanji(card)}</div>
              <div style={{ marginTop: 6 }}><KanjiReading item={card} size={fullscreen ? 14 : 12} /></div>
            </div>
          ) : (
            <div style={{ fontSize: fullscreen ? 14 : 12, color: C.faint }}>Tap to reveal meaning</div>
          )}
        </div>
      </div>

      </div>

      {/* Grading only makes sense once the card is flipped, so the two
          buttons are disabled rather than absent — the bar keeps its
          height and the card underneath never moves. */}
      <div className="study-footer">
        <div className="study-footer-inner">
        <button
          className="btn-ghost"
          disabled={!flipped}
          onClick={() => next('unknown')}
          style={{ flex: 1, justifyContent: 'center', borderColor: `${C.red}38`, color: C.red, opacity: flipped ? 1 : 0.34 }}
        >
          <RiCloseLine size={16} /> Still learning
        </button>
        <button
          className="btn-primary"
          disabled={!flipped}
          onClick={() => next('know')}
          style={{ flex: 1, justifyContent: 'center', background: C.green, marginLeft: 0 }}
        >
          <RiCheckLine size={16} /> Got it
        </button>
        </div>
      </div>
    </div>
  );
}

function KanjiQuizMode({ kanjiList, progress, onGrade, quizState, setQuizState, fullscreen = false, keyboardEnabled = true }) {
  const { cards = [], options = [], index = 0, selected = null, score = 0, done = false } = quizState;
  const hasCards = cards.length > 0;
  const safeIndex = hasCards ? Math.min(index, cards.length - 1) : 0;
  const card = hasCards ? cards[safeIndex] : null;
  const currentOptions = hasCards ? (options[safeIndex] || getKanjiOptions(card, kanjiList)) : [];
  const isLast = hasCards && safeIndex + 1 >= cards.length;
  const pct = hasCards ? (safeIndex / cards.length) * 100 : 0;

  const next = useCallback(() => {
    if (!hasCards) return;
    if (isLast) {
      setQuizState(prev => ({ ...prev, selected: null, done: true }));
      return;
    }
    setQuizState(prev => ({ ...prev, selected: null, index: prev.index + 1 }));
  }, [hasCards, isLast, setQuizState]);

  useEffect(() => {
    if (done || !keyboardEnabled) return;
    const onKeyDown = event => {
      if (event.key !== 'Enter' || event.repeat || !selected) return;
      event.preventDefault();
      next();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [done, keyboardEnabled, next, selected]);

  if (!hasCards) {
    return <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>Preparing your kanji quiz...</div>;
  }

  if (done) {
    const pctScore = Math.round((score / cards.length) * 100);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: fullscreen ? '62vh' : '45vh' }}>
        <SessionComplete
          scorePct={pctScore}
          passed={pctScore >= 60}
          correct={score}
          total={cards.length}
          label="correct"
          title={pctScore >= 80 ? 'Excellent!' : pctScore >= 60 ? 'Good job!' : 'Keep practicing!'}
          onRetry={() => {
            const nextCards = shuffleItems(cards);
            setQuizState({ cards: nextCards, options: nextCards.map(item => getKanjiOptions(item, kanjiList)), index: 0, selected: null, score: 0, done: false });
          }}
          retryLabel="Try again"
        />
      </div>
    );
  }

  const choose = opt => {
    if (selected) return;
    const correct = opt._id === card._id;
    setQuizState(prev => ({ ...prev, selected: opt, score: correct ? prev.score + 1 : prev.score }));
    onGrade(card, correct);
    playMark(correct ? 'bounce' : 'shake');
  };

  const isRight = selected && selected._id === card._id;

  return (
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': fullscreen ? '560px' : '540px' }}>
      <div className="study-stage-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: C.muted }}>{safeIndex + 1} / {cards.length} · Score: <span style={{ color: C.accent, fontWeight: 800 }}>{score}</span> · Mastery {kanjiMasteryFor(progress, card)}%</span>
        <Tag color={C.yellow}>Meaning</Tag>
      </div>
      <div style={{ marginBottom: 20 }}><ProgressBar value={pct} /></div>

      <div className="ks-card" style={{ padding: fullscreen ? '28px 26px' : '24px', textAlign: 'center', marginBottom: 14, minHeight: fullscreen ? 190 : 170, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 12, color: C.muted, fontWeight: 800, letterSpacing: 0.5, marginBottom: 14 }}>What does this kanji mean?</div>
        <div style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: fullscreen ? 84 : 70, fontWeight: 900, lineHeight: 1 }}>{card.char}</div>
        {/* The reading, same as the writing mode shows. It is a hint toward
            the meaning rather than a giveaway, and having it in one mode but
            not the other made the same character feel like two questions. */}
        <div style={{ marginTop: 12 }}><KanjiReading item={card} size={fullscreen ? 15 : 13} /></div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: fullscreen ? 10 : 9 }}>
        {currentOptions.map((opt, i) => {
          const isCorrect = opt._id === card._id;
          const isSelected = selected?._id === opt._id;
          let bg = C.surface, bc = C.border, col = C.text;
          if (selected) {
            if (isCorrect) { bg = `${C.green}16`; bc = `${C.green}45`; col = C.green; }
            else if (isSelected) { bg = `${C.red}16`; bc = `${C.red}45`; col = C.red; }
          }
          return (
            <button
              key={opt._id || i}
              className="choice-btn"
              disabled={!!selected}
              onClick={() => choose(opt)}
              style={{ background: bg, borderColor: bc, color: col, justifyContent: 'center', padding: fullscreen ? '13px 14px' : '12px', minHeight: fullscreen ? 52 : 48, fontSize: fullscreen ? 16 : 15 }}
            >
              <span style={{ fontWeight: 900 }}>{meaningForKanji(opt)}</span>
              {selected && isCorrect && <RiCheckLine size={14} style={{ color: C.green, marginLeft: 6 }} />}
              {selected && isSelected && !isCorrect && <RiCloseLine size={14} style={{ color: C.red, marginLeft: 6 }} />}
            </button>
          );
        })}
      </div>

      </div>

      {/* Present from the first frame — see .study-footer. Only the
          contents change when you answer, never the height. */}
      <div className={`study-footer${selected ? (isRight ? ' correct' : ' wrong') : ''}`}>
        <div className="study-footer-inner">
        {selected ? (
          <div className="study-feedback">
            <strong>
              {isRight ? <><RiCheckLine size={15} /> Correct</> : <><RiCloseLine size={15} /> Not quite</>}
            </strong>
            <span>{card.char} = {meaningForKanji(card)}</span>
          </div>
        ) : (
          <span className="study-footer-hint">Choose the meaning</span>
        )}

        <button className="btn-primary" disabled={!selected} onClick={next}>
          {isLast ? 'See results' : 'Next'} <RiArrowRightLine />
        </button>
        </div>
      </div>
    </div>
  );
}

function KanjiWritingMode({ progress, onGrade, writingState, setWritingState, fullscreen = false, keyboardEnabled = true }) {
  const { cards = [], index = 0, input = '', result = null, score = 0, done = false, showHint = false } = writingState;
  const inputRef = useRef(null);
  const hasCards = cards.length > 0;
  const safeIndex = hasCards ? Math.min(index, cards.length - 1) : 0;
  const card = hasCards ? cards[safeIndex] : null;
  const isLast = hasCards && safeIndex + 1 >= cards.length;
  const pct = hasCards ? (safeIndex / cards.length) * 100 : 0;

  useEffect(() => {
    if (keyboardEnabled) inputRef.current?.focus();
  }, [index, keyboardEnabled]);

  /* Three outcomes, not two. An exact match is correct and a genuinely
     different word is wrong, but a near miss — "electricty", "mountian" —
     is neither: the learner knew the answer and mistyped it. Marking that
     wrong teaches nothing and costs a point unfairly.

     So a near miss is not graded at all. Nothing is reported to onGrade,
     the input stays editable, and they simply fix it and check again. */
  const check = useCallback(() => {
    const typed = input.trim().toLowerCase();
    if (!typed || !card) return;

    const accepted = csv(card.names || card.meaning).map(v => v.toLowerCase());

    if (accepted.includes(typed)) {
      setWritingState(prev => ({ ...prev, result: 'correct', score: prev.score + 1 }));
      onGrade(card, true);
      return;
    }

    const best = accepted.reduce((max, answer) => Math.max(max, similarity(typed, answer)), 0);
    if (best >= NEAR_MISS) {
      setWritingState(prev => ({ ...prev, result: 'close' }));
      return;
    }

    setWritingState(prev => ({ ...prev, result: 'wrong' }));
    onGrade(card, false);
  }, [card, input, onGrade, setWritingState]);

  const next = useCallback(() => {
    if (!hasCards) return;
    setWritingState(prev => ({
      ...prev,
      input: '',
      result: null,
      showHint: false,
      done: isLast ? true : prev.done,
      index: isLast ? prev.index : prev.index + 1,
    }));
  }, [hasCards, isLast, setWritingState]);

  useEffect(() => {
    if (done || !keyboardEnabled) return;
    const onKeyDown = event => {
      if (event.key !== 'Enter' || event.repeat) return;
      event.preventDefault();
      if (result && result !== 'close') next();
      else if (input.trim()) check();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [check, done, input, keyboardEnabled, next, result]);

  if (!hasCards) {
    return <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>Preparing your writing session...</div>;
  }

  if (done) {
    const pctScore = Math.round((score / cards.length) * 100);
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: fullscreen ? '62vh' : '45vh' }}>
        <SessionComplete
          scorePct={pctScore}
          passed={pctScore >= 60}
          correct={score}
          total={cards.length}
          label="correct"
          title={pctScore >= 80 ? 'Excellent!' : pctScore >= 60 ? 'Good job!' : 'Keep practicing!'}
          onRetry={() => setWritingState({ cards: shuffleItems(cards), index: 0, input: '', result: null, score: 0, done: false, showHint: false })}
          retryLabel="Try again"
        />
      </div>
    );
  }

  return (
    <div className={`study-stage${fullscreen ? ' fullscreen' : ''}`} style={{ '--stage-w': fullscreen ? '500px' : '460px' }}>
      <div className="study-stage-body">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 12, color: C.muted }}>{safeIndex + 1} / {cards.length}</span>
        <span style={{ fontSize: 13, color: C.accent, fontWeight: 800 }}>Score: {score} · Mastery {kanjiMasteryFor(progress, card)}%</span>
      </div>
      <div style={{ marginBottom: 20 }}><ProgressBar value={pct} /></div>

      <div className="ks-card" style={{ padding: fullscreen ? '38px 30px' : '34px 28px', textAlign: 'center', marginBottom: 14, minHeight: fullscreen ? 200 : 180, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: "'Noto Sans JP',sans-serif", fontSize: fullscreen ? 92 : 78, fontWeight: 900, lineHeight: 1 }}>{card.char}</div>
        <div style={{ marginTop: 14 }}><KanjiReading item={card} size={fullscreen ? 15 : 13} /></div>
      </div>

      <div style={{ marginBottom: 12 }}>
        <label style={{ fontSize: 12, color: C.muted, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', display: 'block', marginBottom: 8 }}>
          Type the English meaning
        </label>
        <input
          ref={inputRef}
          className="ks-input"
          placeholder="e.g. water"
          value={input}
          onChange={e => setWritingState(prev => ({ ...prev, input: e.target.value, result: null }))}
          disabled={result === 'correct' || result === 'wrong'}
          style={{
            fontSize: fullscreen ? 22 : 18,
            textAlign: 'center',
            fontWeight: 800,
            padding: fullscreen ? 18 : undefined,
            borderColor: result === 'correct' ? C.green
              : result === 'wrong' ? C.red
                : result === 'close' ? C.yellow : undefined,
          }}
        />
      </div>

      {/* Reserved even after checking: dropping this row collapsed ~30px
          and the centred stage slid everything upward. */}
      <div style={{ textAlign: 'center', marginBottom: 12, minHeight: 18, visibility: (result && result !== 'close') ? 'hidden' : 'visible' }}>
        <span
          style={{ fontSize: 12, color: C.muted, cursor: 'pointer', textDecoration: 'underline' }}
          onClick={() => setWritingState(prev => ({ ...prev, showHint: !prev.showHint }))}
        >
          {showHint ? `Answer: ${meaningForKanji(card)}` : 'Show hint'}
        </span>
      </div>

      </div>

      <div className={`study-footer${result ? ` ${result}` : ''}`}>
        <div className="study-footer-inner">
        {result ? (
          <div className="study-feedback">
            <strong>
              {result === 'correct'
                ? <><RiCheckLine size={15} /> Correct</>
                : result === 'close'
                  ? <><RiErrorWarningLine size={15} /> Almost — check your spelling</>
                  : <><RiCloseLine size={15} /> Not quite</>}
            </strong>
            {/* The answer is deliberately withheld on a near miss. Showing it
                would turn "fix your typo" into "copy this", which is the one
                thing this state exists to avoid. Show hint is still there. */}
            <span>{result === 'close' ? 'Close enough that it looks like a typo — edit and check again.' : `${card.char} = ${meaningForKanji(card)}`}</span>
          </div>
        ) : (
          <span className="study-footer-hint">Type what it means, then check</span>
        )}

        {result && result !== 'close' ? (
          <button className="btn-primary" onClick={next}>
            {isLast ? 'See results' : 'Next'} <RiArrowRightLine />
          </button>
        ) : (
          <button className="btn-primary" onClick={check} disabled={!input.trim()}>
            {result === 'close' ? 'Check again' : 'Check'} <RiCheckLine />
          </button>
        )}
        </div>
      </div>
    </div>
  );
}

function FullscreenAtmosphere() {
  const glyphs = ['日', '月', '火', '水', '木', '金', '土', '人', '山', '川', '空', '語', '学', '本', '年', '先', '名', '文', '道', '心'];
  return (
    <div className="kitsu-atmosphere kitsu-atmosphere-fullscreen" aria-hidden="true">
      {glyphs.map((glyph, i) => <span key={`${glyph}-${i}`}>{glyph}</span>)}
    </div>
  );
}

const KANJI_MODES = [
  { key: 'chart', label: 'Chart', icon: RiGridLine },
  { key: 'flashcards', label: 'Flashcards', icon: RiFlashlightLine },
  { key: 'quiz', label: 'Quiz', icon: RiFileTextLine },
  { key: 'writing', label: 'Writing', icon: RiQuillPenLine },
];

const EMPTY_KANJI_FLASHCARD = { cards: [], index: 0, flipped: false, known: [], unknown: [], done: false };
const EMPTY_KANJI_QUIZ = { cards: [], options: [], index: 0, selected: null, score: 0, done: false };
const EMPTY_KANJI_WRITING = { cards: [], index: 0, input: '', result: null, score: 0, done: false, showHint: false };

export function Kanji() {
  const { level } = useAppLevel();
  const [kanjiList, setKanjiList] = useState([]);
  const [sel, setSel] = useState(null);
  const [mode, setMode] = useState('chart');
  const [fullscreen, setFullscreen] = useState(false);
  const [sessionKey, setSessionKey] = useState(0);
  const [progress, setProgress] = useState({ kanji: {} });
  const [loadingSession, setLoadingSession] = useState(false);
  const [flashcardState, setFlashcardState] = useState(EMPTY_KANJI_FLASHCARD);
  const [quizState, setQuizState] = useState(EMPTY_KANJI_QUIZ);
  const [writingState, setWritingState] = useState(EMPTY_KANJI_WRITING);

  /* Watched here rather than inside each mode, for two reasons: the modes
     flip `done` from inside a state updater, and anything with a side
     effect in there fires twice under StrictMode; and all three states
     live here, so this is the only place that can see "a session ended"
     whichever mode ran it. Putting the hook in each mode would also mean
     three streak lookups on every visit.

     The hook fires celebrate() itself, which is why there is no longer a
     separate effect doing it here. */
  const kanjiSessionDone = flashcardState.done || quizState.done || writingState.done;
  const { celebration, dismiss: dismissCelebration } = useStreakCelebration(kanjiSessionDone);

  /* Chart mode is browsing, not a session, so it never guards. */
  const kanjiStarted = flashcardState.known?.length > 0 || flashcardState.unknown?.length > 0
    || quizState.index > 0 || writingState.index > 0;
  useStudyGuard(kanjiStarted && !kanjiSessionDone);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const activeMode = KANJI_MODES.find(m => m.key === mode);

  useEffect(() => {
    Promise.all([
      getKanji({ level, limit: 500 }),
      getKanjiProgress(),
    ])
      .then(([kanjiRes, progressRes]) => {
        const list = Array.isArray(kanjiRes.data) ? kanjiRes.data : [];
        const savedProgress = progressRes.data || { kanji: {} };
        setProgress(savedProgress);
        setKanjiList(list.map(item => ({ ...item, mastery: kanjiMasteryFor(savedProgress, item) })));
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [level]);

  useEffect(() => {
    if (mode === 'chart') {
      setLoadingSession(false);
      setFlashcardState(EMPTY_KANJI_FLASHCARD);
      setQuizState(EMPTY_KANJI_QUIZ);
      setWritingState(EMPTY_KANJI_WRITING);
      return;
    }

    let alive = true;
    setLoadingSession(true);
    setLoadError('');

    getKanjiSession({ level })
      .then(res => {
        if (!alive) return;
        const session = Array.isArray(res.data) && res.data.length ? res.data : makeKanjiSession(kanjiList, sessionKey);
        if (res.progress) setProgress(res.progress);
        if (mode === 'flashcards') {
          setFlashcardState({
            cards: shuffleItems(session),
            index: 0,
            flipped: false,
            known: [],
            unknown: [],
            done: false,
          });
        } else if (mode === 'quiz') {
          const cards = shuffleItems(session);
          setQuizState({
            cards,
            options: cards.map(card => getKanjiOptions(card, kanjiList)),
            index: 0,
            selected: null,
            score: 0,
            done: false,
          });
        } else if (mode === 'writing') {
          setWritingState({
            cards: shuffleItems(session),
            index: 0,
            input: '',
            result: null,
            score: 0,
            done: false,
            showHint: false,
          });
        }
      })
      .catch(err => {
        if (!alive) return;
        setLoadError(err.message);
        const fallback = makeKanjiSession(kanjiList, sessionKey);
        if (mode === 'flashcards') {
          setFlashcardState({
            cards: shuffleItems(fallback),
            index: 0,
            flipped: false,
            known: [],
            unknown: [],
            done: false,
          });
        } else if (mode === 'quiz') {
          const cards = shuffleItems(fallback);
          setQuizState({ cards, options: cards.map(card => getKanjiOptions(card, kanjiList)), index: 0, selected: null, score: 0, done: false });
        } else if (mode === 'writing') {
          setWritingState({ cards: shuffleItems(fallback), index: 0, input: '', result: null, score: 0, done: false, showHint: false });
        }
      })
      .finally(() => {
        if (alive) setLoadingSession(false);
      });

    return () => { alive = false; };
  }, [mode, sessionKey, kanjiList, level]);

  useEffect(() => {
    if (mode !== 'flashcards') {
      setFlashcardState(EMPTY_KANJI_FLASHCARD);
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'quiz') {
      if (mode !== 'quiz') setQuizState(EMPTY_KANJI_QUIZ);
      return;
    }
  }, [mode]);

  useEffect(() => {
    if (mode !== 'writing') {
      if (mode !== 'writing') setWritingState(EMPTY_KANJI_WRITING);
      return;
    }
  }, [mode]);

  useEffect(() => {
    if (!fullscreen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [fullscreen]);

  const handleKanjiGrade = useCallback((card, correct) => {
    if (!card?._id) return;

    updateKanjiProgress({ kanjiId: card._id, correct })
      .then(res => {
        if (res.data) setProgress(res.data);
      })
      .catch(() => { });
  }, []);

  if (loading) return <Spinner />;
  if (error) return <ErrorMsg message={error} />;

  const switchMode = key => {
    setMode(key);
    setSel(null);
    if (key !== 'chart') setSessionKey(value => value + 1);
  };

  const renderMode = (isFullscreen = false) => (
    <>
      {mode === 'chart' && <KanjiChartMode kanjiList={kanjiList} selected={sel} setSelected={setSel} progress={progress} fullscreen={isFullscreen} />}
      {mode === 'flashcards' && (
        <KanjiFlashcardMode
          progress={progress}
          onGrade={handleKanjiGrade}
          flashcardState={flashcardState}
          setFlashcardState={setFlashcardState}
          fullscreen={isFullscreen}
        />
      )}
      {mode === 'quiz' && (
        <KanjiQuizMode
          kanjiList={kanjiList}
          progress={progress}
          onGrade={handleKanjiGrade}
          quizState={quizState}
          setQuizState={setQuizState}
          fullscreen={isFullscreen}
          keyboardEnabled={!fullscreen || isFullscreen}
        />
      )}
      {mode === 'writing' && (
        <KanjiWritingMode
          progress={progress}
          onGrade={handleKanjiGrade}
          writingState={writingState}
          setWritingState={setWritingState}
          fullscreen={isFullscreen}
          keyboardEnabled={!fullscreen || isFullscreen}
        />
      )}
    </>
  );

  return (
    <div style={{ padding: 32 }} className="anim-fade-up">
      <div style={{ marginBottom: 22 }}>
        <h1 style={{ fontSize: 21, fontWeight: 900, letterSpacing: -0.4, marginBottom: 4 }}>Kanji</h1>
        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
          <Tag color={C.accent}>N5</Tag>
          <span style={{ fontSize: 12, color: C.muted }}>{kanjiList.length} characters · chart, flashcards, quiz, and writing practice</span>
        </div>
      </div>

      <div className="kanji-mode-toolbar" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24, flexWrap: 'wrap' }}>
        <div className="kanji-mode-strip" style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', border: `1px solid ${C.border}`, borderRadius: 12, padding: 4, width: 'fit-content' }}>
          {KANJI_MODES.map(m => (
            <KanjiModeTab key={m.key} icon={m.icon} label={m.label} active={mode === m.key} onClick={() => switchMode(m.key)} />
          ))}
        </div>
        <button
          className="btn-ghost"
          onClick={() => setFullscreen(true)}
          title={`Open ${activeMode?.label || 'mode'} full screen`}
          style={{ width: 42, height: 42, padding: 0, justifyContent: 'center' }}
        >
          <RiFullscreenLine size={18} />
        </button>
      </div>

      {loadError && (
        <div style={{ color: C.red, fontSize: 12, fontWeight: 800, marginBottom: 14 }}>
          {loadError}
        </div>
      )}

        {loadingSession && mode !== 'chart' ? (
          <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
            Preparing your adaptive {activeMode?.label?.toLowerCase()} session...
          </div>
        ) : renderMode()}

      {fullscreen && createPortal(
        <div
          className="study-fullscreen-shell kanji-fullscreen-shell"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1200,
            background: C.bg,
            display: 'flex',
            flexDirection: 'column',
            overflow: mode === 'chart' ? 'auto' : 'hidden',
          }}
        >
          <FullscreenAtmosphere />
          <div
            className="study-fullscreen-header kanji-fullscreen-header"
            style={{
            height: 64,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '0 26px',
            borderBottom: `1px solid ${C.border}`,
            background: 'rgba(8,8,14,0.94)',
            backdropFilter: 'blur(18px)',
            position: 'relative',
            zIndex: 2,
          }}
          >
            <div>
              <div style={{ fontSize: 15, fontWeight: 900 }}>{activeMode?.label}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 3 }}>
                <Tag color={C.accent}>N5 Kanji</Tag>
                <span style={{ color: C.muted, fontSize: 12 }}>{kanjiList.length} characters</span>
              </div>
            </div>
            <button className="btn-icon study-fullscreen-close kanji-fullscreen-close" onClick={() => setFullscreen(false)} title="Close full screen">
              <RiCloseLine size={18} />
            </button>
          </div>

          <div
            style={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              width: '100%',
              maxWidth: mode === 'chart' ? 1180 : 880,
              margin: '0 auto',
              padding: mode === 'chart' ? '32px 24px 60px' : '24px 24px 48px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: mode === 'chart' ? 'flex-start' : 'center',
            }}
          >
            {loadingSession && mode !== 'chart' ? (
              <div className="ks-card" style={{ padding: 32, textAlign: 'center', color: C.muted, fontWeight: 800 }}>
                Preparing your adaptive {activeMode?.label?.toLowerCase()} session...
              </div>
            ) : renderMode(true)}
          </div>
        </div>,
        document.body
      )}

      {celebration && (
        <StreakCelebration
          streak={celebration.streak}
          lastStudied={celebration.lastStudied}
          onContinue={dismissCelebration}
        />
      )}
    </div>
  );
}
